import { useCallback } from 'react';

// Helper function moved from ClientOrderPanel
function findPersonalizePageIndex(pages) {
  let foundIndex = -1;
  if (!Array.isArray(pages)) return foundIndex;
  for (let i = 0; i < pages.length; i++) {
    const pg = pages[i];
    const hasPerso =
      (pg.texts || []).some((t) => t.flags?.personalize) ||
      (pg.images || []).some((im) => im.flags?.personalize) ||
      (pg.svgs || []).some((sv) => sv.flags?.personalize);
    if (hasPerso) {
      foundIndex = i;
      break;
    }
  }
  return foundIndex;
}

export function useClientOrderActions(
  backendUrl,
  publicKey,
  order,
  setOrder,
  showPopup,
  hidePopup,
  guestsTextMap, // Needed for saving guests
  sendNoteRef // Needed for sending note
  // States like guestsLoadingFileId, duplicatingFileId etc. remain in the component
) {

  // --- Preview PDF ---
  const previewPdf = useCallback(async (downloadKey, setShowEditorOverlay, setEditorOverlayProps) => {
    console.log("[useClientOrderActions] previewPdf =>", downloadKey);
    showPopup({ title: "Generowanie podglądu", message: "Proszę czekać...", type: 'loading' });

    try {
      // 1. Pobierz informacje o projekcie, aby uzyskać projectId
      const projectInfoUrl = `${backendUrl}/client/projects-key/${downloadKey}/history/latest`;
      console.log("[useClientOrderActions] previewPdf => GET project history latest =>", projectInfoUrl);
      const projectInfoRes = await fetch(projectInfoUrl);
      if (!projectInfoRes.ok) {
        throw new Error(`Błąd pobierania informacji o projekcie (status=${projectInfoRes.status})`);
      }
      const projectInfo = await projectInfoRes.json();
      const projectId = projectInfo.id;

      // 2. Pobierz najnowszą historię projektu, aby sprawdzić, czy jest już wycentrowany
      const historyUrl = `${backendUrl}/client/projects-key/${downloadKey}/history/latest`;
      console.log("[useClientOrderActions] previewPdf => GET history =>", historyUrl);
      const historyRes = await fetch(historyUrl);
      if (!historyRes.ok) {
        throw new Error(`Błąd pobierania historii projektu (status=${historyRes.status})`);
      }
      const historyData = await historyRes.json();
      
      // 3. Sprawdź, czy projekt jest już wycentrowany - zachowujemy tylko tę informację
      if (historyData.autoCentered) {
        console.log("[useClientOrderActions] previewPdf => Project already auto-centered, generating PDF directly");
      } else {
        // 4. Sprawdź, czy projekt wymaga autocentrowania (ma pola z flagą personalize) - tylko do logów
        const hasPersonalization = (historyData.jsonData?.pages || []).some(page => 
          (page.texts || []).some(text => text.flags?.personalize && text.alignment === 'center')
        );
        
        if (hasPersonalization) {
          console.log("[useClientOrderActions] previewPdf => Project needs auto-centering, but skipping as requested");
        } else {
          console.log("[useClientOrderActions] previewPdf => Project doesn't need auto-centering");
        }
      }
      
      // Zawsze generuj PDF bezpośrednio, niezależnie od stanu autocentrowania
      await generatePdf(downloadKey);
    } catch (err) {
      console.error("[useClientOrderActions] previewPdf =>", err);
      hidePopup();
      showPopup({ title: "Błąd podglądu", message: err.message || "Nie udało się wygenerować podglądu PDF.", type: 'error', size: 'compact' });
    }
  }, [backendUrl, publicKey, showPopup, hidePopup]);

  // Funkcja pomocnicza do generowania PDF
  const generatePdf = async (downloadKey) => {
    try {
      const url = `${backendUrl}/client/orders/${publicKey}/preview-pdf/${downloadKey}`;
      console.log("[useClientOrderActions] generatePdf => GET =>", url);
      const res = await fetch(url);
      if (!res.ok) {
        throw new Error(`Błąd generowania podglądu PDF. status=${res.status}`);
      }
      
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = "podglad_klient.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(blobUrl);
      hidePopup();
    } catch (err) {
      console.error("[useClientOrderActions] generatePdf =>", err);
      hidePopup();
      showPopup({ title: "Błąd podglądu", message: "Nie udało się wygenerować podglądu PDF.", type: 'error', size: 'compact' });
    }
  };

  // --- Fetch Guests ---
  const fetchGuests = useCallback(async (fileObj, setGuestsLoadingFileId, setGuestsTextMap) => {
    if (!fileObj?.id) {
      console.warn("[useClientOrderActions] fetchGuests: Brak fileObj.id");
      return;
    }
    setGuestsLoadingFileId(fileObj.id);
    try {
      const getUrl = `${backendUrl}/client/projects/${fileObj.id}/history/latest`;
      console.log("[useClientOrderActions] fetchGuests => GET =>", getUrl);
      const r = await fetch(getUrl);
      if (!r.ok) {
        throw new Error(`Błąd pobierania JSON (public) (status=${r.status}), projectId=${fileObj.id}`);
      }
      const data = await r.json();
      if (!data.jsonData) {
        throw new Error("Brak jsonData w odpowiedzi (public).");
      }
      const pages = data.jsonData.pages || [];
      let lineArr = [];
      const foundPageIndex = findPersonalizePageIndex(pages);
      if (foundPageIndex >= 0) {
        lineArr = pages[foundPageIndex].personalizacja || [];
      } else {
        const firstPageWithPerso = pages.find((pg) => Array.isArray(pg.personalizacja));
        if (firstPageWithPerso) {
          lineArr = firstPageWithPerso.personalizacja;
        }
      }
      // Obsługa zarówno starych projektów (stringi) jak i nowych (obiekty z QR)
      const guestNames = (lineArr || []).map(item => {
        if (typeof item === 'string') {
          return item; // Stare projekty - stringi
        } else if (typeof item === 'object' && item !== null && item.name) {
          return item.name; // Nowe projekty - obiekty z name i qrUrl
        }
        return ''; // Fallback dla nieprawidłowych danych
      }).filter(name => name.trim() !== '');
      
      setGuestsTextMap((prev) => ({ ...prev, [fileObj.id]: guestNames.join("\n") }));
    } catch (err) {
      console.error("[useClientOrderActions] fetchGuests =>", err);
      showPopup({ title: "Błąd pobierania", message: "Nie udało się pobrać listy gości.", type: 'error', size: 'compact' });
    } finally {
      setGuestsLoadingFileId(null);
    }
  }, [backendUrl, showPopup]);

  // --- Save Guests ---
  const saveGuests = useCallback(async (fileObj, setGuestsLoadingFileId, onSuccessCallback) => { // Dodano onSuccessCallback
    if (!fileObj?.id) {
      console.warn("[useClientOrderActions] saveGuests: Brak fileObj.id");
      return;
    }
    
    // Funkcja pomocnicza do zachowania qrUrl podczas aktualizacji listy gości
    const preserveGuestQrUrls = (newGuestNames, existingPersonalizacja) => {
      return newGuestNames.map(guestName => {
        // Sprawdź czy gość już istnieje i ma qrUrl
        const existingGuest = (existingPersonalizacja || []).find(item => {
          if (typeof item === 'string') {
            return item === guestName;
          } else if (typeof item === 'object' && item !== null && item.name) {
            return item.name === guestName;
          }
          return false;
        });
        
        if (existingGuest && typeof existingGuest === 'object' && existingGuest.qrUrl) {
          // Zachowaj istniejący qrUrl
          return { name: guestName, qrUrl: existingGuest.qrUrl };
        } else {
          // Nowy gość lub gość bez qrUrl - zwróć string (kompatybilność wsteczna)
          return guestName;
        }
      });
    };

    setGuestsLoadingFileId(fileObj.id);
    try {
      const getUrl = `${backendUrl}/client/projects/${fileObj.id}/history/latest`;
      console.log("[useClientOrderActions] saveGuests => GET =>", getUrl);
      const getRes = await fetch(getUrl);
      if (!getRes.ok) throw new Error(`Błąd pobierania JSON (public) przed zapisem, status=${getRes.status}`);
      const getData = await getRes.json();
      if (!getData.jsonData) throw new Error("Brak jsonData (przed zapisem, public).");

      let pages = getData.jsonData.pages || [];
      const lines = (guestsTextMap[fileObj.id] || "").split("\n").map((l) => l.trim());
      let foundPageIndex = findPersonalizePageIndex(pages);

      if (foundPageIndex >= 0) {
        // Zachowaj istniejące qrUrl podczas aktualizacji listy gości
        const existingPersonalizacja = pages[foundPageIndex].personalizacja || [];
        const updatedPersonalizacja = preserveGuestQrUrls(lines, existingPersonalizacja);
        
        pages[foundPageIndex].personalizacja = updatedPersonalizacja;
      } else {
        let done = false;
        pages = pages.map((pg) => {
          if (!done && Array.isArray(pg.personalizacja)) {
            pg.personalizacja = preserveGuestQrUrls(lines, pg.personalizacja);
            done = true;
          }
          return pg;
        });
        if (!done && pages.length > 0) {
          pages[0].personalizacja = preserveGuestQrUrls(lines, pages[0].personalizacja || []);
        }
      }

      const finalJson = { pages };
      const saveUrl = `${backendUrl}/client/projects/${fileObj.id}/history`;
      console.log("[useClientOrderActions] saveGuests => POST =>", saveUrl);
      const saveRes = await fetch(saveUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ jsonData: finalJson, saveType: "manual" })
      });
      if (!saveRes.ok) throw new Error(`Błąd zapisu JSON (public) (status=${saveRes.status}).`);
      const saveData = await saveRes.json();
      if (!saveData.success) throw new Error(saveData.message || "Nie udało się zapisać listy gości.");
      showPopup({ message: "Zapisano listę gości.", type: 'success', size: 'compact' });
      if (onSuccessCallback) { // Wywołanie callbacku po sukcesie
        onSuccessCallback(fileObj.id);
      }
    } catch (err) {
      console.error("[useClientOrderActions] saveGuests =>", err);
      showPopup({ title: "Błąd zapisu", message: "Nie udało się zapisać listy gości.", type: 'error', size: 'compact' });
    } finally {
      setGuestsLoadingFileId(null);
    }
  }, [backendUrl, guestsTextMap, showPopup]);

  // --- Generate Personalized Project ---
  const doGeneratePersonalized = useCallback(async (fileObj, existingChild) => {
    showPopup({ title: "Generowanie projektu", message: "Proszę czekać...", type: 'loading' });
    try {
      const url = `${backendUrl}/client/orders/${publicKey}/personalize?downloadKey=${fileObj.downloadKey}`;
      const body = { parentFileId: fileObj.id, childFileId: existingChild?.id || null };
      console.log("[useClientOrderActions] doGeneratePersonalized => POST =>", url);
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      });
      if (!res.ok) throw new Error(`Błąd generowania projektu z listą gości (status=${res.status}) (client).`);
      const data = await res.json();
      if (!data.success) throw new Error(data.message || "Nie udało się wygenerować projektu spersonalizowanego (client).");
      if (data.files) {
        setOrder(prevOrder => ({ ...prevOrder, files: data.files }));
      }
      showPopup({ message: "Wygenerowano projekt z listą gości.", type: 'success', size: 'compact' });
    } catch (err) {
      console.error("[useClientOrderActions] doGeneratePersonalized =>", err);
      showPopup({ title: "Błąd generowania", message: "Nie udało się wygenerować projektu spersonalizowanego.", type: 'error', size: 'compact' });
    }
  }, [backendUrl, publicKey, setOrder, showPopup]);

  const generatePersonalized = useCallback((fileObj) => {
    console.log("[useClientOrderActions] generatePersonalized =>", fileObj);
    const existingChild = (order?.files || []).find((f) => f.parentFileId === fileObj.id);
    if (existingChild) {
      showPopup({
        title: "Uwaga!",
        message: (<>Projekt z listą gości już istnieje (<strong>{existingChild.fileName}</strong>).<br />Czy chcesz go nadpisać? Poprzedni plik zostanie usunięty.</>),
        type: 'confirm',
        confirmText: 'Tak, nadpisz',
        cancelText: 'Anuluj',
        size: 'compact',
        onConfirm: () => doGeneratePersonalized(fileObj, existingChild),
      });
    } else {
      doGeneratePersonalized(fileObj, null);
    }
  }, [order, showPopup, doGeneratePersonalized]);

  // --- Duplicate Project ---
  const duplicateProject = useCallback(async (fileObj, setDuplicatingFileId) => {
    setDuplicatingFileId(fileObj.id);
    showPopup({ title: "Duplikowanie projektu", message: "Proszę czekać...", type: 'loading' });
    try {
      const url = `${backendUrl}/client/orders/${publicKey}/duplicate-project`;
      console.log(`[useClientOrderActions] duplicateProject => POST => ${url}`);
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fileId: fileObj.id })
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({ message: "Błąd serwera bez dodatkowych informacji." }));
        throw new Error(`Błąd duplikowania projektu (status=${res.status}): ${errorData.message}`);
      }
      const result = await res.json();
      if (!result.success || !result.files) throw new Error(result.message || "Nie udało się zduplikować projektu (brak plików w odpowiedzi).");
      setOrder(prevOrder => ({ ...prevOrder, files: result.files }));
      showPopup({ message: "Projekt został pomyślnie zduplikowany.", type: 'success', size: 'compact' });
    } catch (err) {
      console.error("[useClientOrderActions] duplicateProject =>", err);
      showPopup({ title: "Błąd duplikowania", message: "Wystąpił błąd podczas próby duplikowania projektu.", type: 'error', size: 'compact' });
    } finally {
      setDuplicatingFileId(null);
    }
  }, [backendUrl, publicKey, setOrder, showPopup]);

  // --- Add Project From Template ---
  const addProjectFromTemplate = useCallback(async (templateId, setAddingProjectFromTemplateId) => {
    setAddingProjectFromTemplateId(templateId);
    showPopup({ title: "Dodawanie projektu", message: "Proszę czekać...", type: 'loading' });
    try {
      const url = `${backendUrl}/client/orders/${publicKey}/add-project-from-template`;
      console.log(`[useClientOrderActions] addProjectFromTemplate => POST => ${url}`);
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ templateId })
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({ message: "Błąd serwera bez dodatkowych informacji." }));
        throw new Error(`Błąd dodawania projektu z szablonu (status=${res.status}): ${errorData.message}`);
      }
      const result = await res.json();
      if (!result.success || !result.files) throw new Error(result.message || "Nie udało się dodać projektu z szablonu (brak plików w odpowiedzi).");
      setOrder(prevOrder => ({ ...prevOrder, files: result.files }));
      showPopup({ message: "Projekt został pomyślnie dodany z szablonu.", type: 'success', size: 'compact' });
    } catch (err) {
      console.error("[useClientOrderActions] addProjectFromTemplate =>", err);
      showPopup({ title: "Błąd dodawania", message: "Wystąpił błąd podczas próby dodania projektu z szablonu.", type: 'error', size: 'compact' });
    } finally {
      setAddingProjectFromTemplateId(null);
    }
  }, [backendUrl, publicKey, setOrder, showPopup]);

  // --- Delete Project ---
  const doDeleteProject = useCallback(async (fileObj, setDeletingFileId) => {
    setDeletingFileId(fileObj.id);
    showPopup({ title: "Usuwanie projektu", message: "Proszę czekać...", type: 'loading' });
    try {
      const url = `${backendUrl}/client/orders/${publicKey}/projects/${fileObj.id}`;
      console.log(`[useClientOrderActions] doDeleteProject => DELETE => ${url}`);
      const res = await fetch(url, { method: "DELETE" });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({ message: "Błąd serwera bez dodatkowych informacji." }));
        throw new Error(`Błąd usuwania projektu (status=${res.status}): ${errorData.message}`);
      }
      const result = await res.json();
      if (!result.success) throw new Error(result.message || "Nie udało się usunąć projektu.");
      setOrder(prevOrder => ({ ...prevOrder, files: prevOrder.files.filter(f => f.id !== fileObj.id) }));
      showPopup({ message: "Projekt został pomyślnie usunięty.", type: 'success', size: 'compact' });
    } catch (err) {
      console.error("[useClientOrderActions] doDeleteProject =>", err);
      showPopup({ title: "Błąd usuwania", message: "Wystąpił błąd podczas próby usunięcia projektu.", type: 'error', size: 'compact' });
    } finally {
      setDeletingFileId(null);
    }
  }, [backendUrl, publicKey, setOrder, showPopup]);

  const deleteProject = useCallback((fileObj, setDeletingFileId) => {
    // Sprawdzenie, czy projekt jest rodzicem projektu z listą gości (projektu zależnego)
    const isParentOfPersonalizedChild = order?.files?.some(
      (f) => f.parentFileId === fileObj.id && f.hasPersonalization 
             // f.hasPersonalization wskazuje, że dziecko jest projektem spersonalizowanym (z listą gości)
    );

    if (isParentOfPersonalizedChild) {
      // fileObj jest projektem głównym i ma dziecko, które jest projektem spersonalizowanym.
      // W tej sytuacji nie można usunąć projektu głównego.
      const childProject = order.files.find(f => f.parentFileId === fileObj.id && f.hasPersonalization);
      const message = `Nie można usunąć tego projektu (${fileObj.fileName}), ponieważ istnieje do niego wygenerowany projekt z listą gości (${childProject?.fileName || 'nieznany'}). Najpierw usuń projekt z listą gości.`;
      
      showPopup({
        title: "Operacja niedozwolona",
        message: message,
        type: 'info',
        confirmText: 'OK',
        size: 'compact',
        onConfirm: hidePopup,
      });
    } else {
      // Projekt nie jest rodzicem spersonalizowanego dziecka.
      // Może to być:
      // 1. Projekt główny bez spersonalizowanych dzieci (nawet jeśli sam ma `hasPersonalization=true` - co oznaczałoby, że jest to plik szablonu, który może być personalizowany, ale nie jest jeszcze wynikiem personalizacji).
      // 2. Projekt zależny (wynik personalizacji, `fileObj.hasPersonalization=true` i `fileObj.parentFileId` istnieje).
      // 3. Zwykły projekt bez personalizacji i bez dzieci.
      // W każdym z tych przypadków można go usunąć.
      showPopup({
        title: "Potwierdzenie usunięcia",
        message: (<>Czy na pewno chcesz trwale usunąć projekt <strong>{fileObj.fileName}</strong>? Tej operacji nie można cofnąć.</>),
        type: 'confirm',
        confirmText: 'Tak, usuń',
        cancelText: 'Anuluj',
        size: 'compact',
        onConfirm: () => doDeleteProject(fileObj, setDeletingFileId),
        onCancel: hidePopup,
      });
    }
  }, [order, showPopup, hidePopup, doDeleteProject]);

  // --- Send To Print ---
  // Note: openSendPopup and sendToPrint require setters for component-specific state
  // (showSendToPrintPopup, sendEmail, isSendingToPrint, sendPopupValidationError)
  // These might be better left in the component or passed as arguments if the popup is also extracted.
  // For now, let's keep them here but acknowledge they need state setters from the component.

  const openSendPopup = useCallback(async (
    setSendEmail,
    setIsSendingToPrint,
    setSendPopupValidationError,
    setShowSendToPrintPopup
  ) => {
    try {
      setSendEmail("");
      if (sendNoteRef.current) sendNoteRef.current.value = ""; // Reset ref value if possible
      setIsSendingToPrint(false);
      setSendPopupValidationError("");
      const cleanedPublicKey = String(publicKey || '').replace(/[^a-zA-Z0-9]/g, '');
      const url = `${backendUrl}/client/orders/${cleanedPublicKey}`;
      console.log(`[useClientOrderActions] openSendPopup: Fetching order: ${url}`);
      const resp = await fetch(url);
      if (!resp.ok) throw new Error("Błąd pobierania zamówienia.");
      const orderData = await resp.json();
      const currentEmail = orderData.customOrderId || "";
      setSendEmail(currentEmail);
      setShowSendToPrintPopup(true);
    } catch (ex) {
      console.error("[useClientOrderActions] openSendPopup =>", ex);
      showPopup({ title: "Błąd", message: "Nie udało się pobrać danych zamówienia.", type: 'error', size: 'compact' });
    }
  }, [backendUrl, publicKey, showPopup, sendNoteRef]);

  const sendToPrint = useCallback(async (
    sendEmail,
    setIsSendingToPrint,
    setSendPopupValidationError,
    setShowSendToPrintPopup
  ) => {
    setSendPopupValidationError("");
    if (!sendEmail.trim() || !sendEmail.includes("@")) {
      setSendPopupValidationError("Niepoprawny adres e-mail!");
      return;
    }
    setIsSendingToPrint(true);
    try {
      const cleanedPublicKey = String(publicKey || '').replace(/[^a-zA-Z0-9]/g, '');

      // 1. Check/Update Email
      const checkUrl = `${backendUrl}/client/orders/${cleanedPublicKey}`;
      const checkRes = await fetch(checkUrl);
      if (!checkRes.ok) throw new Error("Błąd pobierania zamówienia przed wysyłką.");
      const checkData = await checkRes.json();
      const oldEmail = checkData.customOrderId || "";
      if (oldEmail.trim() !== sendEmail.trim()) {
        const patchEmailUrl = `${backendUrl}/client/orders/${cleanedPublicKey}/email`;
        const emailBody = { email: sendEmail };
        const patchEmailRes = await fetch(patchEmailUrl, {
          method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(emailBody)
        });
        if (!patchEmailRes.ok) throw new Error("Błąd zapisu nowego adresu e-mail w zamówieniu.");
      }

      // 2. Update file statuses and order
      const orderAgainUrl = `${backendUrl}/client/orders/${cleanedPublicKey}`;
      const orderAgainRes = await fetch(orderAgainUrl);
      if (!orderAgainRes.ok) throw new Error("Błąd ponownego pobierania zamówienia przed zmianą statusów.");
      const orderAgainData = await orderAgainRes.json();
      const oldFiles = orderAgainData.files || [];
      const newFilesWithAcceptedStatus = oldFiles.map((f) => ({ ...f, statusId: 1 }));
      const patchOrderUrl = `${backendUrl}/client/orders/${cleanedPublicKey}`;
      const patchOrderRes = await fetch(patchOrderUrl, {
        method: "PATCH", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ files: newFilesWithAcceptedStatus, customOrderId: sendEmail, sentToPrint: true })
      });
      if (!patchOrderRes.ok) throw new Error("Błąd ustawiania statusu plików na 'Zaakceptowany'.");
      const updatedOrderData = await patchOrderRes.json();

      // 3. Run trigger
      const triggerUrl = `${backendUrl}/client/orders/${cleanedPublicKey}/run-trigger`;
      const triggerBody = { triggerName: "nowe_zamowienie", tresc_od_klienta: sendNoteRef.current?.value || "" }; // Access ref value
      const trigRes = await fetch(triggerUrl, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(triggerBody)
      });
      if (!trigRes.ok) console.error("Błąd uruchamiania triggera mailowego, ale zamówienie zostało zaakceptowane.");

      // 4. Update state, close popup, show success
      const finalOrderData = { ...updatedOrderData, sentToPrint: true };
      setOrder(finalOrderData);
      setShowSendToPrintPopup(false);
      showPopup({ title: "Wysłano do druku!", message: "Zamówienie zostało zaakceptowane i przekazane do realizacji. Sprawdź swoją pocztę e-mail.", type: 'success', size: 'compact' });
    } catch (err) {
      console.error("[useClientOrderActions] sendToPrint =>", err);
      setSendPopupValidationError(err.message || "Nie udało się wysłać zamówienia do druku.");
    } finally {
      setIsSendingToPrint(false);
    }
  }, [backendUrl, publicKey, setOrder, showPopup, sendNoteRef]);


  // --- Update Quantity (Client Panel) ---
  const updateQuantity = useCallback(async (fileId, newQuantity, setUpdatingQuantityFileId) => {
    setUpdatingQuantityFileId(fileId); // Indicate loading for this specific file
    // Basic validation
    const quantityNum = parseInt(newQuantity, 10);
    if (isNaN(quantityNum) || quantityNum < 1) {
      showPopup({ title: "Błąd", message: "Ilość musi być liczbą większą lub równą 1.", type: 'error', size: 'compact' });
      setUpdatingQuantityFileId(null);
      return;
    }

    try {
      const url = `${backendUrl}/client/orders/${publicKey}/projects/${fileId}/quantity`;
      console.log(`[useClientOrderActions] updateQuantity => PATCH => ${url}`);
      const res = await fetch(url, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ quantity: quantityNum })
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({ message: "Błąd serwera bez dodatkowych informacji." }));
        // Handle specific error for personalized items
        if (res.status === 400 && errorData.message.includes("personalizacj")) {
           throw new Error(errorData.message); // Throw the specific message
        }
        throw new Error(`Błąd aktualizacji ilości (status=${res.status}): ${errorData.message}`);
      }

      const result = await res.json();
      if (!result.success || !result.updatedFile) {
        throw new Error(result.message || "Nie udało się zaktualizować ilości (brak danych pliku w odpowiedzi).");
      }

      // Update the specific file in the order state
      setOrder(prevOrder => {
        const updatedFiles = (prevOrder.files || []).map(file =>
          file.id === fileId ? result.updatedFile : file
        );
        return { ...prevOrder, files: updatedFiles };
      });

      // Optional: Show success popup (might be too noisy for frequent updates)
      // showPopup({ message: "Ilość zaktualizowana.", type: 'success', duration: 1500 });

    } catch (err) {
      console.error("[useClientOrderActions] updateQuantity =>", err);
      showPopup({ title: "Błąd aktualizacji", message: err.message || "Wystąpił błąd podczas próby aktualizacji ilości.", type: 'error', size: 'compact' });
      // Optional: Revert quantity in UI or refetch order data on error
    } finally {
      setUpdatingQuantityFileId(null); // Stop loading indicator for this file
    }
  }, [backendUrl, publicKey, setOrder, showPopup]);

  // --- Run "Save Panel" Trigger ---
  const runSavePanelTrigger = useCallback(async (clientEmail, panelLink) => {
    // Nie potrzebujemy tutaj settera ładowania, bo jest zarządzany w ClientOrderPanel
    showPopup({ title: "Wysyłanie linku", message: "Proszę czekać...", type: 'loading' });
    try {
      const url = `${backendUrl}/client/orders/${publicKey}/run-trigger`;
      const body = {
        triggerName: "zapisz_panel",
        context: { // Przekazujemy dodatkowe dane w kontekście
          clientEmail: clientEmail,
          panel_link: panelLink
        }
      };
      console.log(`[useClientOrderActions] runSavePanelTrigger => POST => ${url}`);
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({ message: "Błąd serwera bez dodatkowych informacji." }));
        throw new Error(`Błąd uruchamiania triggera 'zapisz_panel' (status=${res.status}): ${errorData.message}`);
      }

      const result = await res.json();
      if (!result.success) {
        throw new Error(result.message || "Nie udało się uruchomić triggera 'zapisz_panel'.");
      }

      // Pokaż sukces - komunikat z ClientOrderPanel został usunięty, więc pokazujemy go tutaj
      const emailSavedMessage = clientEmail !== order?.customOrderId ? 'Adres e-mail został zapisany.' : ''; // Sprawdź czy email był zapisywany
       showPopup({
         title: "Link wysłany!",
         message: `Link do panelu został wysłany na adres ${clientEmail}. ${emailSavedMessage}`,
         type: 'success',
         size: 'compact'
       });

    } catch (err) {
      console.error("[useClientOrderActions] runSavePanelTrigger =>", err);
      // Błąd jest już pokazywany w ClientOrderPanel w bloku catch saveEmailAndRunTrigger
      // showPopup({ title: "Błąd wysyłania", message: err.message || "Wystąpił błąd podczas próby wysłania linku.", type: 'error' });
      // Rzucamy błąd dalej, aby ClientOrderPanel mógł go obsłużyć i pokazać odpowiedni komunikat
      throw err;
    } finally {
      // Nie zarządzamy tutaj stanem ładowania, więc nie ma potrzeby finally
      // hidePopup(); // Popup ładowania zostanie zamknięty przez popup sukcesu/błędu
    }
  }, [backendUrl, publicKey, order, showPopup]); // Dodano order do zależności

  // --- Fetch Latest History ID ---
  const fetchLatestHistoryId = useCallback(async (downloadKey) => {
    try {
      const url = `${backendUrl}/client/projects-key/${downloadKey}/history/latest`;
      console.log(`[useClientOrderActions] fetchLatestHistoryId => GET => ${url}`);
      const res = await fetch(url);
      if (!res.ok) {
        throw new Error(`Błąd pobierania najnowszej historii (status=${res.status})`);
      }
      return await res.json();
    } catch (err) {
      console.error("[useClientOrderActions] fetchLatestHistoryId =>", err);
      showPopup({ title: "Błąd", message: "Nie udało się pobrać informacji o historii projektu.", type: 'error', size: 'compact' });
      return null; // Zwróć null w przypadku błędu
    }
  }, [backendUrl, showPopup]);

  // --- Request Graphic Changes ---
  const requestGraphicChanges = useCallback(async (downloadKey, clientRequestText, clientEmail) => {
    try {
      const url = `${backendUrl}/client/projects/${downloadKey}/request-graphic-changes`;
      console.log(`[useClientOrderActions] requestGraphicChanges => POST => ${url}`);
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ clientRequestText, clientEmail })
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({ message: "Błąd serwera." }));
        throw new Error(errorData.message || `Błąd wysyłania prośby do grafika (status=${res.status})`);
      }
      return await res.json();
    } catch (err) {
      console.error("[useClientOrderActions] requestGraphicChanges =>", err);
      // Popup błędu jest zarządzany w komponencie wywołującym (ClientOrderPanel)
      throw err; // Rzuć błąd dalej, aby komponent mógł go obsłużyć
    }
  }, [backendUrl]);

  // --- AI Modify Project ---
  const aiModifyProject = useCallback(async (downloadKey, clientRequestText, previousHistoryId, setIsAiProcessingCallback) => {
    if (typeof setIsAiProcessingCallback === 'function') {
      setIsAiProcessingCallback(true);
    }
    try {
      const url = `${backendUrl}/client/projects/${downloadKey}/ai-modify`;
      console.log(`[useClientOrderActions] aiModifyProject => POST => ${url}`);
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ clientRequestText, previousHistoryId })
      });
      // Nie sprawdzamy res.ok tutaj, ponieważ backend może zwrócić sukces nawet z błędami AI
      const result = await res.json();
      if (!result.success && !result.newHistoryId) { // Krytyczny błąd, jeśli nie ma sukcesu i nie ma newHistoryId
         throw new Error(result.message || "Krytyczny błąd podczas modyfikacji AI.");
      }
      return result; // Zwróć cały obiekt result
    } catch (err) {
      console.error("[useClientOrderActions] aiModifyProject =>", err);
      throw err; // Rzuć błąd dalej
    } finally {
      if (typeof setIsAiProcessingCallback === 'function') {
        setIsAiProcessingCallback(false);
      }
    }
  }, [backendUrl]);

  // --- Restore AI Modified Version ---
  const restoreAiModifiedVersion = useCallback(async (downloadKey, historyEntryIdToRestore) => {
    try {
      const url = `${backendUrl}/client/projects/${downloadKey}/history/restore`;
      console.log(`[useClientOrderActions] restoreAiModifiedVersion => POST => ${url}`);
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ historyEntryIdToRestore })
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({ message: "Błąd serwera." }));
        throw new Error(errorData.message || `Błąd przywracania wersji (status=${res.status})`);
      }
      return await res.json();
    } catch (err) {
      console.error("[useClientOrderActions] restoreAiModifiedVersion =>", err);
      throw err; // Rzuć błąd dalej
    }
  }, [backendUrl]);


  return {
    // saveFileName, // Needs editingFileId, tempFileName, handleCancelEditFileName from component
    previewPdf,
    fetchGuests,
    saveGuests,
    generatePersonalized,
    duplicateProject,
    addProjectFromTemplate,
    deleteProject,
    openSendPopup,
    sendToPrint,
    updateQuantity,
    runSavePanelTrigger, // Eksportujemy nową funkcję
    fetchLatestHistoryId,
    requestGraphicChanges,
    aiModifyProject,
    restoreAiModifiedVersion,
  };
}
